// src/components/FileList.js

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabase.js';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { ShareModal } from '../modals/ShareModal';
import { localDb } from '../../localDb.js';
import { SummaryModal } from '../modals/SummaryModal';

import { PreviewModal } from '../modals/PreviewModal';
import { VersionHistoryModal } from '../modals/VersionHistoryModal';
import { FaFolder, FaPencilAlt, FaTrash } from 'react-icons/fa';
import '../modals/SummaryModal.css';

export const FileList = ({ files, isLoading, searchTerm, onOpenFileChat, onRefresh, user }) => {
  const [offlineFiles, setOfflineFiles] = useState(new Set());
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] = useState(false);
  const [selectedFileVersions, setSelectedFileVersions] = useState([]);
  const [latestVersionId, setLatestVersionId] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [currentSummary, setCurrentSummary] = useState('');
  const [editingFileId, setEditingFileId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);


  const [selectedFilter, setSelectedFilter] = useState('All');

  // Folders layout states
  const [viewMode, setViewMode] = useState('files'); // files | folders
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderNameInput, setNewFolderNameInput] = useState('');
  const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [renameFolderNameInput, setRenameFolderNameInput] = useState('');
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);
  const [folderTags, setFolderTags] = useState([]); // holds folder tags to preserve during normal tag edits

  const [localFolders, setLocalFolders] = useState(() => {
    if (user) {
      const localStr = localStorage.getItem(`custom_folders_list_${user.id}`);
      return localStr ? JSON.parse(localStr) : [];
    }
    return [];
  });

  useEffect(() => {
    if (user) {
      const localStr = localStorage.getItem(`custom_folders_list_${user.id}`);
      setLocalFolders(localStr ? JSON.parse(localStr) : []);
    }
  }, [user]);

  const [isDeleteFolderOpen, setIsDeleteFolderOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isMovingFile, setIsMovingFile] = useState(false);

  const counts = useMemo(() => {
    const grouped = {};
    [...files].forEach(file => {
      const name = file.fileName;
      if (!grouped[name] || new Date(file.uploadedAt) > new Date(grouped[name].uploadedAt)) {
        grouped[name] = file;
      }
    });

    const uniqueFiles = Object.values(grouped);

    let pdf = 0, doc = 0, sheet = 0, img = 0, other = 0;
    uniqueFiles.forEach(file => {
      const name = file.fileName.toLowerCase();
      if (name.endsWith('.pdf')) pdf++;
      else if (name.endsWith('.docx') || name.endsWith('.doc') || name.endsWith('.txt')) doc++;
      else if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) sheet++;
      else if (name.match(/\.(png|jpe?g|gif|webp|svg)$/)) img++;
      else other++;
    });
    return { All: uniqueFiles.length, PDFs: pdf, Documents: doc, Spreadsheets: sheet, Images: img, Other: other };
  }, [files]);

  useEffect(() => {
    const checkOfflineStatus = async () => {
      const offlineKeys = await localDb.files.toCollection().keys();
      setOfflineFiles(new Set(offlineKeys));
    };
    checkOfflineStatus();
  }, [files]);

  const filteredFiles = useMemo(() => {
    // 1. Group by fileName to get the latest versions
    const grouped = {};
    [...files].forEach(file => {
      const name = file.fileName;
      if (!grouped[name] || new Date(file.uploadedAt) > new Date(grouped[name].uploadedAt)) {
        grouped[name] = file;
      }
    });

    let uniqueFiles = Object.values(grouped).sort((a, b) => {
      const timeA = new Date(a.uploadedAt);
      const timeB = new Date(b.uploadedAt);
      return timeB - timeA;
    });

    // 2. Apply active filter
    if (selectedFilter !== 'All') {
      uniqueFiles = uniqueFiles.filter(file => {
        const name = file.fileName.toLowerCase();
        if (selectedFilter === 'PDFs') return name.endsWith('.pdf');
        if (selectedFilter === 'Documents') return name.endsWith('.docx') || name.endsWith('.doc') || name.endsWith('.txt');
        if (selectedFilter === 'Spreadsheets') return name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv');
        if (selectedFilter === 'Images') return !!name.match(/\.(png|jpe?g|gif|webp|svg)$/);
        if (selectedFilter === 'Other') {
          return !name.endsWith('.pdf') &&
            !name.endsWith('.docx') && !name.endsWith('.doc') && !name.endsWith('.txt') &&
            !name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv') &&
            !name.match(/\.(png|jpe?g|gif|webp|svg)$/);
        }
        return true;
      });
    }

    if (!searchTerm) return uniqueFiles;

    const lowercasedFilter = searchTerm.toLowerCase();
    return uniqueFiles.filter(file => {
      const nameMatch = file.fileName && file.fileName.toLowerCase().includes(lowercasedFilter);
      const tagMatch = file.tags && file.tags.some(tag => tag.toLowerCase().includes(lowercasedFilter));
      return nameMatch || tagMatch;
    });
  }, [files, selectedFilter, searchTerm]);

  const closeModal = () => {
    setSelectedFile(null);
    setIsShareModalOpen(false);
    setIsSummaryModalOpen(false);
    setIsPreviewModalOpen(false);
    setIsVersionHistoryModalOpen(false);
  };

  const openPreviewModal = (file) => { setSelectedFile(file); setIsPreviewModalOpen(true); };
  const openShareModal = (file) => { setSelectedFile(file); setIsShareModalOpen(true); };

  const openVersionHistoryModal = (file) => {
    const allVersions = files.filter(f => f.fileName === file.fileName).sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
    setSelectedFileVersions(allVersions);
    setLatestVersionId(file.id);
    setSelectedFile(file);
    setIsVersionHistoryModalOpen(true);
  };

  const handlePromoteVersion = async (version) => {
    const toastId = toast.loading("Making version current...");
    try {
      // Omit id and uploadedAt so Supabase generates new ones, effectively duplicating the record as the "newest"
      const { id, uploadedAt, ...versionDataToCopy } = version;

      const { error } = await supabase
        .from('files')
        .insert([versionDataToCopy]);

      if (error) throw error;

      toast.dismiss(toastId);
      toast.success("Version promoted successfully!");
      closeModal();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error(error);
      toast.dismiss(toastId);
      toast.error("Failed to promote version.");
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setFileToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const proceedWithDelete = async () => {
    if (!fileToDelete) return;
    const toastId = toast.loading(`Deleting "${fileToDelete.fileName}"...`);
    try {
      // Delete from database
      await supabase.from('files').delete().eq('id', fileToDelete.id);

      // Extract storage path from URL to delete from storage
      if (fileToDelete.fileUrl) {
        const urlParts = fileToDelete.fileUrl.split('/public/files/');
        if (urlParts.length === 2) {
          const storagePath = urlParts[1];
          await supabase.storage.from('files').remove([storagePath]);
        }
      }

      await removeFileFromOffline(fileToDelete.id, fileToDelete.fileName, false);
      toast.dismiss(toastId);
      toast.success(`"${fileToDelete.fileName}" has been deleted.`);
      closeDeleteModal();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to delete file.");
    }
  };

  const openCategoryModal = (file) => {
    setSelectedFile(file);
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setSelectedFile(null);
    setIsCategoryModalOpen(false);
  };

  const handleSaveCategory = async (cat) => {
    if (!selectedFile) return;
    const toastId = toast.loading(cat ? `Categorizing as ${cat}...` : "Removing category...");
    try {
      await supabase.from('files').update({ category: cat }).eq('id', selectedFile.id);
      toast.dismiss(toastId);
      toast.success(cat ? `Category set to ${cat}!` : "Category removed!");
      closeCategoryModal();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Failed to set category.");
    }
  };

  const makeFileOffline = async (file) => {
    const toastId = toast.loading('Saving file for offline use...');
    try {
      if (!file.fileUrl) throw new Error("No URL found for this file.");
      const response = await fetch(file.fileUrl);
      const fileBlob = await response.blob();
      await localDb.files.put({ id: file.id, name: file.fileName, fileBlob });
      setOfflineFiles(prev => new Set(prev).add(file.id));
      toast.dismiss(toastId);
      toast.success(`"${file.fileName}" is now available offline.`);
    } catch (error) {
      console.error("Error making file offline:", error);
      toast.dismiss(toastId);
      toast.error("Could not save file offline.");
    }
  };

  const removeFileFromOffline = async (fileId, fileName, showAlert = true) => {
    try {
      await localDb.files.delete(fileId);
      setOfflineFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
      if (showAlert) toast.success(`"${fileName}" removed from offline storage.`);
    } catch (error) {
      if (showAlert) toast.error("Could not remove offline file.");
    }
  };

  const handleApiRequest = async (file, endpoint, bodyPayload) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Authentication error.");

      // Point to our new Vercel serverless functions
      const functionUrl = `/api/${endpoint}`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Optionally pass the token if you add auth to the API routes later
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ data: { ...bodyPayload, fileUrl: file.fileUrl } }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Server error");
      return result;
    } catch (error) {
      toast.error(`An error occurred: ${error.message}`);
      throw error;
    }
  };

  const [isSummarizing, setIsSummarizing] = useState(false);

  const handleSummarize = async (file, forceRegenerate = false) => {
    const name = file.fileName.toLowerCase();
    const isAiSupported = name.endsWith('.pdf') || 
                          name.endsWith('.txt') || 
                          name.endsWith('.csv') || 
                          name.endsWith('.json') || 
                          name.endsWith('.md') || 
                          name.endsWith('.docx') || 
                          name.endsWith('.xlsx') || 
                          name.endsWith('.pptx') || 
                          !!name.match(/\.(png|jpe?g|gif|webp|svg)$/);
    if (!isAiSupported) return toast.error("Summarization is only supported for PDFs, Text files, Images, and Office documents.");
    setSelectedFile(file);
    setIsSummaryModalOpen(true);

    // 0ms DB Cache check: if summary already exists, load it instantly!
    if (file.summary && !forceRegenerate) {
      setCurrentSummary(file.summary);
      return;
    }

    setCurrentSummary('Generating summary...');
    setIsSummarizing(true);
    try {
      const result = await handleApiRequest(file, "summarizeFile", {});
      setCurrentSummary(result.data.summary);

      // Save summary to database
      await supabase.from('files').update({ summary: result.data.summary }).eq('id', file.id);
      if (onRefresh) onRefresh();
    } catch (error) {
      setCurrentSummary(`Failed to generate summary: ${error.message}`);
    } finally {
      setIsSummarizing(false);
    }
  };




  const [activeTags, setActiveTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isGeneratingAiTags, setIsGeneratingAiTags] = useState(false);

  const handleFetchAiSuggestions = async (file) => {
    if (!file || !file.fileUrl) return toast.error("File details not available.");
    setIsGeneratingAiTags(true);
    const toastId = toast.loading("Genie AI is analyzing the document...");
    try {
      const result = await handleApiRequest(file, "generateTags", { existingTags: activeTags });
      if (result && result.data && result.data.tags) {
        setAiSuggestions(result.data.tags);
        toast.dismiss(toastId);
        toast.success("New AI tags recommended successfully!");
      } else {
        throw new Error("No tags returned from Genie AI.");
      }
    } catch (error) {
      console.error(error);
      toast.dismiss(toastId);
      toast.error(`Failed to generate tags: ${error.message || 'Unknown error'}`);
    } finally {
      setIsGeneratingAiTags(false);
    }
  };

  // Folders computed mapping
  const getFileFolder = (file) => {
    if (!file) return 'Other';
    const folderTag = file.tags && file.tags.find(t => t.startsWith('folder:'));
    if (folderTag) {
      return folderTag.replace('folder:', '');
    }
    return file.category || 'Other';
  };

  const customFolders = useMemo(() => {
    const activeFolders = new Set();
    files.forEach(file => {
      const folderTag = file.tags && file.tags.find(t => t.startsWith('folder:'));
      if (folderTag) {
        activeFolders.add(folderTag.replace('folder:', ''));
      }
    });

    localFolders.forEach(f => activeFolders.add(f));

    return Array.from(activeFolders);
  }, [files, localFolders]);

  const allFolders = useMemo(() => {
    const defaults = ['Work', 'College', 'Personal', 'Finance', 'Study', 'Other'];
    return [...defaults, ...customFolders.filter(f => !defaults.includes(f))];
  }, [customFolders]);

  const folderGroups = useMemo(() => {
    const groups = {};
    allFolders.forEach(f => {
      groups[f] = [];
    });
    
    filteredFiles.forEach(file => {
      const folderName = getFileFolder(file);
      if (!groups[folderName]) {
        groups[folderName] = [];
      }
      groups[folderName].push(file);
    });
    
    return groups;
  }, [filteredFiles, allFolders]);

  // Folder Handlers
  const handleCreateFolderSubmit = (e) => {
    e.preventDefault();
    const name = newFolderNameInput.trim();
    if (!name) return toast.error("Folder name cannot be empty.");
    if (name.includes(':') || name.startsWith('folder:')) {
      return toast.error("Invalid characters in folder name.");
    }
    
    if (!user) return toast.error("User session not found.");
    const key = `custom_folders_list_${user.id}`;
    const localStr = localStorage.getItem(key);
    const foldersList = localStr ? JSON.parse(localStr) : [];
    if (!foldersList.includes(name)) {
      foldersList.push(name);
      localStorage.setItem(key, JSON.stringify(foldersList));
      setLocalFolders(foldersList);
    }
    
    setNewFolderNameInput('');
    setIsCreateFolderOpen(false);
    toast.success(`Folder "${name}" created!`);
  };

  const handleDeleteFolderClick = (e, folderName) => {
    e.stopPropagation();
    setFolderToDelete(folderName);
    setIsDeleteFolderOpen(true);
  };

  const proceedDeleteFolder = async () => {
    if (!folderToDelete || !user) return;
    const folderName = folderToDelete;
    setIsDeletingFolder(true);
    const toastId = toast.loading(`Deleting folder "${folderName}"...`);
    try {
      const key = `custom_folders_list_${user.id}`;
      const localStr = localStorage.getItem(key);
      const foldersList = localStr ? JSON.parse(localStr) : [];
      const updated = foldersList.filter(f => f !== folderName);
      localStorage.setItem(key, JSON.stringify(updated));
      setLocalFolders(updated);

      const filesToUpdate = files.filter(f => f.tags && f.tags.includes(`folder:${folderName}`));
      if (filesToUpdate.length > 0) {
        const updatePromises = filesToUpdate.map(file => {
          const newTags = file.tags.filter(t => t !== `folder:${folderName}`);
          return supabase.from('files').update({ tags: newTags }).eq('id', file.id);
        });
        await Promise.all(updatePromises);
      }

      toast.dismiss(toastId);
      toast.success(`Folder "${folderName}" deleted.`);
      if (expandedFolder === folderName) setExpandedFolder(null);
      setIsDeleteFolderOpen(false);
      setFolderToDelete(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Failed to delete folder.");
    } finally {
      setIsDeletingFolder(false);
    }
  };

  const handleRenameFolderClick = (e, folderName) => {
    e.stopPropagation();
    setFolderToRename(folderName);
    setRenameFolderNameInput(folderName);
    setIsRenameFolderOpen(true);
  };

  const handleRenameFolderSubmit = async (e) => {
    e.preventDefault();
    const newName = renameFolderNameInput.trim();
    const oldName = folderToRename;
    if (!newName) return toast.error("Folder name cannot be empty.");
    if (newName === oldName) return setIsRenameFolderOpen(false);
    if (newName.includes(':') || newName.startsWith('folder:')) {
      return toast.error("Invalid characters in folder name.");
    }

    if (!user) return toast.error("User session not found.");

    setIsRenamingFolder(true);
    const toastId = toast.loading(`Renaming to "${newName}"...`);
    try {
      const key = `custom_folders_list_${user.id}`;
      const localStr = localStorage.getItem(key);
      const foldersList = localStr ? JSON.parse(localStr) : [];
      const updated = foldersList.map(f => f === oldName ? newName : f);
      if (!updated.includes(newName)) updated.push(newName);
      localStorage.setItem(key, JSON.stringify(updated));
      setLocalFolders(updated);

      const filesToUpdate = files.filter(f => f.tags && f.tags.includes(`folder:${oldName}`));
      if (filesToUpdate.length > 0) {
        const updatePromises = filesToUpdate.map(file => {
          const newTags = file.tags.map(t => t === `folder:${oldName}` ? `folder:${newName}` : t);
          return supabase.from('files').update({ tags: newTags }).eq('id', file.id);
        });
        await Promise.all(updatePromises);
      }

      toast.dismiss(toastId);
      toast.success(`Folder renamed to "${newName}"!`);
      setIsRenameFolderOpen(false);
      if (expandedFolder === oldName) setExpandedFolder(newName);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Failed to rename folder.");
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const openMoveModal = (file) => {
    setFileToMove(file);
    setIsMoveModalOpen(true);
  };

  const handleMoveFile = async (targetFolder) => {
    if (!fileToMove) return;
    setIsMovingFile(true);
    const toastId = toast.loading(`Moving "${fileToMove.fileName}" to ${targetFolder}...`);
    try {
      const otherTags = (fileToMove.tags || []).filter(t => !t.startsWith('folder:'));
      let newTags = [...otherTags];
      if (targetFolder !== fileToMove.category) {
        newTags.push(`folder:${targetFolder}`);
      }
      
      await supabase.from('files').update({ tags: newTags }).eq('id', fileToMove.id);
      toast.dismiss(toastId);
      toast.success(`Moved to "${targetFolder}"!`);
      setIsMoveModalOpen(false);
      setFileToMove(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Failed to move file.");
    } finally {
      setIsMovingFile(false);
    }
  };

  const handleEditTagsClick = (file) => {
    setEditingFileId(file.id);
    setSelectedFile(file);
    
    // Preserve folder tags
    const fTags = (file.tags || []).filter(t => t.startsWith('folder:'));
    setFolderTags(fTags);
    
    // Display only normal tags in editing sandbox
    const displayTags = (file.tags || []).filter(t => !t.startsWith('folder:'));
    setActiveTags(displayTags);
    
    setNewTagInput('');
    setAiSuggestions([]);
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setActiveTags([]);
    setFolderTags([]);
    setNewTagInput('');
    setAiSuggestions([]);
  };

  const handleAddSandboxTag = (tagText = newTagInput) => {
    const tag = tagText.trim();
    if (tag && !activeTags.includes(tag)) {
      setActiveTags(prev => [...prev, tag]);
    }
    setNewTagInput('');
  };

  const handleRemoveSandboxTag = (tagToRemove) => {
    setActiveTags(prev => prev.filter(t => t !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddSandboxTag();
    }
  };

  const getSmartTagSuggestions = (file) => {
    const suggestions = new Set();
    const name = file.fileName.toLowerCase();

    // Extension
    if (name.endsWith('.pdf')) suggestions.add('PDF');
    if (name.endsWith('.docx') || name.endsWith('.doc')) suggestions.add('Document');
    if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) suggestions.add('Spreadsheet');
    if (name.match(/\.(png|jpe?g|gif|webp|svg)$/)) suggestions.add('Image');

    // Keywords mapping
    if (name.includes('invoice') || name.includes('bill') || name.includes('receipt')) {
      suggestions.add('Finance');
      suggestions.add('Invoice');
    }
    if (name.includes('resume') || name.includes('cv') || name.includes('portfolio')) {
      suggestions.add('Career');
      suggestions.add('Resume');
    }
    if (name.includes('homework') || name.includes('lecture') || name.includes('class') || name.includes('assignment') || name.includes('question') || name.includes('interview')) {
      suggestions.add('Academic');
      suggestions.add('Study');
    }
    if (name.includes('report') || name.includes('analysis') || name.includes('research')) {
      suggestions.add('Report');
      suggestions.add('Research');
    }
    if (name.includes('contract') || name.includes('agreement') || name.includes('legal')) {
      suggestions.add('Legal');
      suggestions.add('Contract');
    }

    // Filter out existing active tags
    return Array.from(suggestions).filter(tag => !activeTags.includes(tag));
  };

  const handleSaveTags = async (fileId) => {
    try {
      const mergedTags = [...activeTags, ...folderTags];
      await supabase.from('files').update({ tags: mergedTags }).eq('id', fileId);
      handleCancelEdit();
      toast.success("Tags saved successfully!");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to save tags.");
    }
  };

  const renderFileItem = (file) => {
    const name = file.fileName.toLowerCase();
    const isAiSupported = name.endsWith('.pdf') || 
                          name.endsWith('.txt') || 
                          name.endsWith('.csv') || 
                          name.endsWith('.json') || 
                          name.endsWith('.md') || 
                          name.endsWith('.docx') || 
                          name.endsWith('.xlsx') || 
                          name.endsWith('.pptx') || 
                          !!name.match(/\.(png|jpe?g|gif|webp|svg)$/);
    const fileVersions = files.filter(f => f.fileName === file.fileName);
    const hasHistory = fileVersions.length > 1;

    return (
      <li key={file.id}>
        <div className="file-metadata-col">
          <div className="file-details-stack">
            <div className="file-title-row">
              <button onClick={() => openPreviewModal(file)} className="file-name-button">{file.fileName}</button>
              {file.category ? (
                <span
                  className="category-badge interactive"
                  onClick={() => openCategoryModal(file)}
                  style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  title="Click to change category"
                >
                  {file.category}
                </span>
              ) : (
                <button
                  className="add-category-badge-btn"
                  onClick={() => openCategoryModal(file)}
                  title="Add category"
                >
                  + Add Category
                </button>
              )}
            </div>
            <div className="file-tags-row">
              {file.tags && file.tags
                .filter(tag => !tag.startsWith('folder:'))
                .map(tag => <span key={tag} className="tag-badge">{tag}</span>)}
              <button className="edit-tags-link-btn" onClick={() => handleEditTagsClick(file)}>Edit Tags</button>
            </div>
          </div>
        </div>
        <div className="file-actions-col">
          {isAiSupported && <button className="share-button" onClick={() => onOpenFileChat(file)}>Ask AI</button>}
          {isAiSupported && <button className="share-button" onClick={() => handleSummarize(file)}>Summarize File</button>}
          <button className="share-button" onClick={() => openShareModal(file)}>Share</button>
          <button className="share-button" onClick={() => openMoveModal(file)}>Move</button>
          {hasHistory && <button className="share-button" onClick={() => openVersionHistoryModal(file)}>History</button>}
          {offlineFiles.has(file.id) ? (
            <button className="remove-offline-button" onClick={() => removeFileFromOffline(file.id, file.fileName)}>Remove</button>
          ) : (
            <button className="offline-button" onClick={() => makeFileOffline(file)}>Make Offline</button>
          )}
          <button className="delete-button" onClick={() => handleDeleteClick(file)}>Delete</button>
        </div>
      </li>
    );
  };

  return (
    <div className="file-list-container">
      {/* File/Folder Tabs Header */}
      <div className="file-list-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button 
            onClick={() => { setViewMode('files'); setExpandedFolder(null); }} 
            className="folder-breadcrumb-btn"
            style={{
              color: viewMode === 'files' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              borderBottom: viewMode === 'files' ? '3px solid var(--color-primary)' : '3px solid transparent',
              paddingBottom: '6px'
            }}
          >
            Files
          </button>
          <button 
            onClick={() => setViewMode('folders')} 
            className="folder-breadcrumb-btn"
            style={{
              color: viewMode === 'folders' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              borderBottom: viewMode === 'folders' ? '3px solid var(--color-primary)' : '3px solid transparent',
              paddingBottom: '6px'
            }}
          >
            Folders
          </button>
        </div>

        {viewMode === 'folders' && !expandedFolder && (
          <button 
            onClick={() => setIsCreateFolderOpen(true)}
            style={{
              padding: '10px 18px',
              borderRadius: '12px',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-background)',
              border: 'none',
              fontSize: '12px',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(var(--color-primary-rgb), 0.2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            + New Folder
          </button>
        )}
      </div>

      {viewMode === 'files' ? (
        <>
          <div className="dashboard-filters-wrapper">
            {Object.entries(counts).map(([category, count]) => (
              <button
                key={category}
                className={`filter-btn ${selectedFilter === category ? 'active' : ''}`}
                onClick={() => setSelectedFilter(category)}
              >
                {category} <span className="filter-badge">{count}</span>
              </button>
            ))}
          </div>

          {isLoading ? (
            <p>Loading your files...</p>
          ) : filteredFiles.length > 0 ? (
            <ul>
              {filteredFiles.map(file => renderFileItem(file))}
            </ul>
          ) : (
            <p>No files found. Upload a file to get started!</p>
          )}
        </>
      ) : (
        /* Folders view */
        <>
          {expandedFolder ? (
            /* Inside a folder */
            <div>
              <div className="folder-expanded-header-panel">
                <button className="folder-breadcrumb-btn" onClick={() => setExpandedFolder(null)}>Folders</button>
                <span className="folder-breadcrumb-separator">&gt;</span>
                <span className="folder-breadcrumb-current">{expandedFolder}</span>
              </div>

              {folderGroups[expandedFolder] && folderGroups[expandedFolder].length > 0 ? (
                <ul>
                  {folderGroups[expandedFolder].map(file => renderFileItem(file))}
                </ul>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)', padding: '20px 0' }}>This folder is empty. Move files here to get started!</p>
              )}
            </div>
          ) : (
            /* Grid of all folders */
            <div className="folders-grid">
              {allFolders.map(folderName => {
                const folderFiles = folderGroups[folderName] || [];
                const isDefault = ['Work', 'College', 'Personal', 'Finance', 'Study', 'Other'].includes(folderName);
                
                return (
                  <div key={folderName} className="folder-card" onClick={() => setExpandedFolder(folderName)}>
                    {!isDefault && (
                      <>
                        <button 
                          className="folder-card-top-action edit"
                          onClick={(e) => handleRenameFolderClick(e, folderName)}
                          title="Rename Folder"
                          aria-label="Rename Folder"
                        >
                          <FaPencilAlt />
                        </button>
                        <button 
                          className="folder-card-top-action delete"
                          onClick={(e) => handleDeleteFolderClick(e, folderName)}
                          title="Delete Folder"
                          aria-label="Delete Folder"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                    <div className="folder-icon-wrapper">
                      <FaFolder />
                    </div>
                    <h4 className="folder-name" title={folderName}>{folderName}</h4>
                    <span className="folder-count">
                      {folderFiles.length} {folderFiles.length === 1 ? 'file' : 'files'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      <ShareModal isOpen={isShareModalOpen} onRequestClose={closeModal} file={selectedFile} />
      <SummaryModal
        isOpen={isSummaryModalOpen}
        onRequestClose={closeModal}
        summary={currentSummary}
        fileName={selectedFile?.fileName}
        onRegenerate={() => handleSummarize(selectedFile, true)}
        isGenerating={isSummarizing}
      />
      <PreviewModal isOpen={isPreviewModalOpen} onRequestClose={closeModal} file={selectedFile} />
      <VersionHistoryModal isOpen={isVersionHistoryModalOpen} onRequestClose={closeModal} versions={selectedFileVersions} onPromote={handlePromoteVersion} fileName={selectedFile?.fileName} latestVersionId={latestVersionId} />

      {/* Add New Folder Modal */}
      <Modal
        isOpen={isCreateFolderOpen}
        onRequestClose={() => setIsCreateFolderOpen(false)}
        contentLabel="Create Folder Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '400px' } }}
      >
        <button className="modal-close-btn" onClick={() => setIsCreateFolderOpen(false)} aria-label="Close create folder">&times;</button>
        <h2>Create New Folder</h2>
        <form onSubmit={handleCreateFolderSubmit} style={{ marginTop: '16px' }}>
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="newFolderName">Folder Name</label>
            <input
              id="newFolderName"
              type="text"
              value={newFolderNameInput}
              onChange={(e) => setNewFolderNameInput(e.target.value)}
              placeholder="e.g. Invoices, Homework..."
              required
              autoFocus
              style={{
                width: '100%',
                padding: '14px 18px',
                fontSize: '14px',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.02)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-body)',
                outline: 'none',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxSizing: 'border-box',
                marginTop: '8px'
              }}
            />
          </div>
          <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button 
              type="button" 
              className="cancel-button" 
              onClick={() => setIsCreateFolderOpen(false)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="action-button save-button" 
              style={{ 
                padding: '12px 24px', 
                borderRadius: '12px', 
                backgroundColor: 'var(--color-primary)', 
                color: 'var(--color-background)', 
                border: 'none', 
                fontSize: '13px', 
                fontWeight: '700', 
                cursor: 'pointer', 
                margin: 0 
              }}
            >
              Create Folder
            </button>
          </div>
        </form>
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        isOpen={isRenameFolderOpen}
        onRequestClose={() => { if (!isRenamingFolder) setIsRenameFolderOpen(false); }}
        contentLabel="Rename Folder Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '400px' } }}
      >
        <button 
          className="modal-close-btn" 
          onClick={() => setIsRenameFolderOpen(false)} 
          disabled={isRenamingFolder}
          aria-label="Close rename folder"
        >
          &times;
        </button>
        <h2>Rename Folder</h2>
        <form onSubmit={handleRenameFolderSubmit} style={{ marginTop: '16px' }}>
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="renameFolderName">Folder Name</label>
            <input
              id="renameFolderName"
              type="text"
              value={renameFolderNameInput}
              onChange={(e) => setRenameFolderNameInput(e.target.value)}
              placeholder="New Folder Name..."
              required
              autoFocus
              disabled={isRenamingFolder}
              style={{
                width: '100%',
                padding: '14px 18px',
                fontSize: '14px',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.02)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-body)',
                outline: 'none',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxSizing: 'border-box',
                marginTop: '8px',
                opacity: isRenamingFolder ? 0.6 : 1
              }}
            />
          </div>
          <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button 
              type="button" 
              className="cancel-button" 
              onClick={() => setIsRenameFolderOpen(false)}
              disabled={isRenamingFolder}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="action-button save-button" 
              disabled={isRenamingFolder}
              style={{ 
                padding: '12px 24px', 
                borderRadius: '12px', 
                backgroundColor: 'var(--color-primary)', 
                color: 'var(--color-background)', 
                border: 'none', 
                fontSize: '13px', 
                fontWeight: '700', 
                cursor: isRenamingFolder ? 'not-allowed' : 'pointer', 
                margin: 0,
                opacity: isRenamingFolder ? 0.6 : 1
              }}
            >
              {isRenamingFolder ? 'Renaming...' : 'Rename Folder'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Move File to Folder Modal */}
      <Modal
        isOpen={isMoveModalOpen}
        onRequestClose={() => { if (!isMovingFile) { setIsMoveModalOpen(false); setFileToMove(null); } }}
        contentLabel="Move File Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '440px' } }}
      >
        <button 
          className="modal-close-btn" 
          onClick={() => { setIsMoveModalOpen(false); setFileToMove(null); }} 
          disabled={isMovingFile}
          aria-label="Close move dialog"
        >
          &times;
        </button>
        <h2>Move to Folder</h2>
        <div style={{ margin: '15px 0 25px 0', fontSize: '14px', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
          Choose a destination folder for <strong>{fileToMove?.fileName}</strong>:
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', maxHeight: '300px', overflowY: 'auto', padding: '4px', marginBottom: '20px' }}>
          {allFolders.map(folderName => {
            const currentFolder = getFileFolder(fileToMove);
            const isCurrent = currentFolder === folderName;
            return (
              <button
                key={folderName}
                className="category-select-pill"
                onClick={() => handleMoveFile(folderName)}
                disabled={isCurrent || isMovingFile}
                style={{
                  padding: '14px',
                  borderRadius: '12px',
                  border: isCurrent ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                  backgroundColor: isCurrent ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--color-background)',
                  color: isCurrent ? 'var(--color-primary)' : 'var(--color-text-primary)',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: (isCurrent || isMovingFile) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)',
                  opacity: isCurrent ? 0.65 : 1
                }}
              >
                {folderName}
              </button>
            );
          })}
        </div>
        <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button 
            type="button" 
            className="cancel-button" 
            onClick={() => { setIsMoveModalOpen(false); setFileToMove(null); }}
            disabled={isMovingFile}
            style={{ width: '100%' }}
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Center Delete Folder Confirmation Modal */}
      <Modal
        isOpen={isDeleteFolderOpen}
        onRequestClose={() => { if (!isDeletingFolder) { setIsDeleteFolderOpen(false); setFolderToDelete(null); } }}
        contentLabel="Delete Folder Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '420px' } }}
      >
        <button 
          className="modal-close-btn" 
          onClick={() => { setIsDeleteFolderOpen(false); setFolderToDelete(null); }} 
          disabled={isDeletingFolder}
          aria-label="Close delete dialog"
        >
          &times;
        </button>
        <h2>Confirm Delete Folder</h2>
        <div style={{ margin: '15px 0 25px 0', fontSize: '14px', color: 'var(--color-text-primary)', lineHeight: '1.6' }}>
          Are you sure you want to permanently delete the folder <strong>{folderToDelete}</strong>? Files inside will be moved back to their default category folders. This action cannot be undone.
        </div>
        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button 
            type="button" 
            className="cancel-button" 
            onClick={() => { setIsDeleteFolderOpen(false); setFolderToDelete(null); }}
            disabled={isDeletingFolder}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="action-button"
            onClick={proceedDeleteFolder}
            disabled={isDeletingFolder}
            style={{
              flex: 1,
              padding: '12px 24px',
              borderRadius: '12px',
              backgroundColor: 'var(--color-danger)',
              color: 'white',
              border: 'none',
              fontSize: '13px',
              fontWeight: '700',
              cursor: isDeletingFolder ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.2)',
              opacity: isDeletingFolder ? 0.6 : 1
            }}
          >
            {isDeletingFolder ? 'Deleting...' : 'Delete Folder'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={editingFileId !== null}
        onRequestClose={handleCancelEdit}
        contentLabel="Edit File Tags Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '500px' } }}
      >
        <button className="modal-close-btn" onClick={handleCancelEdit} aria-label="Close tags edit">&times;</button>
        <h2>Edit Tags for "{selectedFile?.fileName}"</h2>

        {selectedFile && (
          <div className="tag-sandbox-container" style={{ marginTop: '10px' }}>
            <div className="tag-sandbox" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px', border: '1px solid var(--color-border)', borderRadius: '12px', minHeight: '60px', marginBottom: '16px', backgroundColor: 'rgba(var(--color-primary-rgb), 0.01)' }}>
              {activeTags.map((tag, idx) => (
                <span key={idx} className="sandbox-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(var(--color-primary-rgb), 0.08)', color: 'var(--color-primary)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
                  {tag}
                  <button type="button" onClick={() => handleRemoveSandboxTag(tag)} className="sandbox-chip-remove" style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: '14px', padding: 0 }}>&times;</button>
                </span>
              ))}
              {activeTags.length === 0 && <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>No active tags.</span>}
            </div>

            <div className="sandbox-add-wrapper" style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <input
                type="text"
                className="sandbox-add-input"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add a tag and press Enter..."
                style={{ flex: 1, padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: '12px', fontSize: '14px', outline: 'none', backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
              />
              <button type="button" className="sandbox-add-btn" onClick={() => handleAddSandboxTag()} style={{ width: '48px', height: '48px', border: 'none', borderRadius: '12px', backgroundColor: 'var(--color-primary)', color: 'var(--color-background)', fontSize: '20px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            </div>

            <div className="suggested-tags-wrapper" style={{ marginBottom: '24px' }}>
              <div className="suggested-tags-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div className="suggested-tags-label" style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--color-text-secondary)', letterSpacing: '0.5px' }}>AI Suggestions</div>
                <button
                  type="button"
                  onClick={() => handleFetchAiSuggestions(selectedFile)}
                  disabled={isGeneratingAiTags}
                  className="generate-ai-tags-btn"
                  style={{
                    background: 'none',
                    border: '1px solid var(--color-primary)',
                    color: 'var(--color-primary)',
                    padding: '4px 10px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isGeneratingAiTags ? 'Generating...' : 'Generate'}
                </button>
              </div>
              
              {isGeneratingAiTags ? (
                <div className="summary-loading-state" style={{ padding: '20px 10px', gap: '12px' }}>
                  <div className="summary-skeleton-wave" style={{ height: '10px' }} />
                  <div className="summary-skeleton-wave" style={{ animationDelay: '0.2s', width: '85%', height: '10px' }} />
                  <div className="summary-skeleton-wave" style={{ animationDelay: '0.4s', width: '90%', height: '10px' }} />
                </div>
              ) : aiSuggestions.length > 0 ? (
                <div className="suggested-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {aiSuggestions.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className="suggested-tag-pill"
                      onClick={() => {
                        handleAddSandboxTag(tag);
                        setAiSuggestions(prev => prev.filter(t => t !== tag));
                      }}
                      style={{
                        background: 'rgba(var(--color-accent-rgb), 0.05)',
                        border: '1px solid rgba(var(--color-accent-rgb), 0.3)',
                        color: 'var(--color-accent)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="suggested-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {getSmartTagSuggestions(selectedFile).map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className="suggested-tag-pill"
                      onClick={() => handleAddSandboxTag(tag)}
                      style={{
                        background: 'none',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text-secondary)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      + {tag}
                    </button>
                  ))}
                  {getSmartTagSuggestions(selectedFile).length === 0 && (
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>Click 'Generate' to fetch custom tags.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button className="action-button save-button" onClick={() => handleSaveTags(editingFileId)} style={{ padding: '12px 24px', borderRadius: '12px', backgroundColor: 'var(--color-primary)', color: 'var(--color-background)', border: 'none', fontSize: '13px', fontWeight: '700', cursor: 'pointer', margin: 0 }}>Save Tags</button>
        </div>
      </Modal>

      {/* Center Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={closeDeleteModal}
        contentLabel="Delete File Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '420px' } }}
      >
        <button className="modal-close-btn" onClick={closeDeleteModal} aria-label="Close delete dialog">&times;</button>
        <h2>Confirm Delete</h2>
        <div style={{ margin: '15px 0 25px 0', fontSize: '14px', color: 'var(--color-text-primary)', lineHeight: '1.6' }}>
          Are you sure you want to permanently delete <strong>{fileToDelete?.fileName}</strong>? This action cannot be undone.
        </div>
        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="action-button delete-button-confirm"
            onClick={proceedWithDelete}
            style={{
              width: '100%',
              padding: '12px 24px',
              borderRadius: '12px',
              backgroundColor: 'var(--color-danger)',
              color: 'white',
              border: 'none',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              margin: 0,
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.2)'
            }}
          >
            Permanently Delete
          </button>
        </div>
      </Modal>

      {/* Center Change Category Modal */}
      <Modal
        isOpen={isCategoryModalOpen}
        onRequestClose={closeCategoryModal}
        contentLabel="Change Category Modal"
        className="modal"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '440px' } }}
      >
        <button className="modal-close-btn" onClick={closeCategoryModal} aria-label="Close category selection">&times;</button>
        <h2>Select Category</h2>
        <div style={{ margin: '15px 0 25px 0', fontSize: '14px', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
          Choose a folder category for <strong>{selectedFile?.fileName}</strong>:
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
          {["Work", "College", "Personal", "Finance", "Study", "Other"].map(cat => {
            const isSelected = selectedFile?.category === cat;
            return (
              <button
                key={cat}
                className="category-select-pill"
                onClick={() => handleSaveCategory(cat)}
                style={{
                  padding: '14px',
                  borderRadius: '12px',
                  border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                  backgroundColor: isSelected ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--color-background)',
                  color: isSelected ? 'var(--color-primary)' : 'var(--color-text-primary)',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)'
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {selectedFile?.category && (
          <button
            className="action-button"
            onClick={() => handleSaveCategory(null)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid var(--color-danger)',
              backgroundColor: 'transparent',
              color: 'var(--color-danger)',
              fontWeight: '700',
              fontSize: '13px',
              cursor: 'pointer',
              marginBottom: '20px',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-body)'
            }}
          >
            Remove Category
          </button>
        )}


      </Modal>
    </div>
  );
};